<template>
  <div class="recueil-view">
    <button class="back-btn" @click="$emit('go-sommaire')">← sommaire</button>

    <div class="recueil-title">{{ recueil.name }}</div>
    <div class="recueil-desc">{{ recueil.desc }}</div>

    <div class="grid">
      <div class="cover" :style="{ height: coverH + 'px' }">
        <img
          v-if="recueil.texts.length"
          class="cover-img"
          :src="recueil.texts[0].illustrationPath"
          :alt="recueil.texts[0].illustrationCaption"
        />
        <span class="cover-label">COUVERTURE DU RECUEIL</span>
      </div>

      <div class="texte-list">
        <div
          v-for="(texte, ti) in recueil.texts"
          :key="texte.title"
          class="texte-row"
          @click="$emit('go-texte', ti)"
        >
          <span class="texte-num">{{ pad2(ti) }}</span>
          <span class="texte-title">{{ texte.title }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import type { RecueilData } from '../types'

defineProps<{ recueil: RecueilData }>()

defineEmits<{
  'go-sommaire': []
  'go-texte': [ti: number]
}>()

function pad2(i: number): string {
  return String(i + 1).padStart(2, '0')
}

const coverH = ref(170)

function syncCoverH() {
  const w = window.innerWidth
  if (w >= 1100) coverH.value = 260
  else if (w >= 640) coverH.value = 220
  else coverH.value = 170
}

onMounted(() => {
  syncCoverH()
  window.addEventListener('resize', syncCoverH)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', syncCoverH)
})
</script>

<style scoped>
.recueil-view {
  box-sizing: border-box;
  min-height: 100vh;
  background: #262C34;
  padding: 40px 30px 60px;
}

.back-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.2em;
  color: #A8C6DE;
}

.back-btn:hover { color: #F2F0EA; }

.recueil-title {
  margin-top: 40px;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 13px;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: #F2F0EA;
}

.recueil-desc {
  margin-top: 12px;
  font-family: 'EB Garamond', serif;
  font-size: 18px;
  font-style: italic;
  line-height: 1.6;
  color: #B7C4CF;
  max-width: 46ch;
}

.grid {
  margin-top: 30px;
  display: grid;
  align-items: start;
  gap: 40px;
  grid-template-columns: 1fr;
}

.cover {
  position: relative;
  background: repeating-linear-gradient(135deg, #2E353E 0 6px, #333B45 6px 12px);
  display: flex;
  align-items: flex-end;
  padding: 12px;
  overflow: hidden;
}

.cover-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.65;
}

.cover-label {
  position: relative;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 9px;
  letter-spacing: 0.14em;
  color: #A8C6DE;
}

.texte-list {
  display: flex;
  flex-direction: column;
}

.texte-row {
  display: flex;
  gap: 12px;
  align-items: baseline;
  padding: 11px 0;
  border-bottom: 1px solid #343B44;
  cursor: pointer;
  transition: opacity 0.15s;
}

.texte-row:hover { opacity: 0.72; }

.texte-num {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  color: #93A3B4;
  width: 22px;
  flex: none;
}

.texte-title {
  font-family: 'EB Garamond', serif;
  font-size: 21px;
  color: #F2F0EA;
  flex: 1;
}

@media (min-width: 640px) {
  .recueil-view { padding: 48px 56px 60px; }
  .grid { grid-template-columns: 300px 1fr; }
}

@media (min-width: 1100px) {
  .recueil-view { padding: 64px 72px 60px; }
  .grid { grid-template-columns: 380px 1fr; }
}
</style>
